import { useCallback, useEffect, useMemo, useState } from 'react';
import { ProCard } from '@ant-design/pro-components';
import AdminProTable from '@/components/admin-pro-table';
import { App, Button, Divider, Popconfirm, Popover, Tooltip } from 'antd';
import { DeleteOutlined, EditOutlined, EyeOutlined, HistoryOutlined, PictureOutlined, SettingOutlined } from '@ant-design/icons';
import Permission from '@/components/permission';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSetState } from 'ahooks';
import { useNavigate, type NavigateOptions } from 'react-router-dom';

import OperationLogDrawer from '@/components/OperationLogDrawer';
import {
    ACTIVITY_1011_CONFIG_KEY,
    ACTIVITY_1011_CONFIG_MODULE,
    ACTIVITY_1011_TASK_CONFIG_PERMISSION,
} from '@/constants/activity-1011';
import {
    THEATER_BANNER_CONFIG_KEY,
    THEATER_BANNER_CONFIG_MODULE,
    THEATER_BANNER_PERMISSION,
} from '@/constants/theater-banner';
import { serviceConfigDelete, serviceConfigList, type ServiceConfigItem } from '@/services/service-config';

import type { ProColumns } from '@ant-design/pro-components';
import type { PaginationProps } from 'antd';

type DetailPageMode = 'create' | 'edit' | 'detail';

const DEFAULT_PAGINATION: PaginationProps = {
    current: 1,
    pageSize: 20,
};

const PageServiceConfig: React.FC = () => {
    const { message } = App.useApp();
    const navigate = useNavigate();
    const { formatMessage } = useIntl();
    const permissionScope = 'System-Management_Configs';
    const detailPath = '/system-management/service-config/detail';
    const bannerEditorPath = '/client-management/banner';
    const activity1011ConfigPath = '/dramas/activity-1011/task-config';
    const addPermission = `${permissionScope}_Create`;
    const editPermission = `${permissionScope}_Update`;
    const deletePermission = `${permissionScope}_Delete`;

    const [pagination, setPagination] = useSetState<PaginationProps>({ ...DEFAULT_PAGINATION });
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState<ServiceConfigItem[]>([]);
    const [deletingId, setDeletingId] = useState<string>();
    const [logDrawerOpen, setLogDrawerOpen] = useState(false);
    const [logBizId, setLogBizId] = useState<string | null>(null);

    const fetchList = useCallback(
        async (page: number = pagination.current as number, pageSize: number = pagination.pageSize as number) => {
            setLoading(true);
            try {
                const { data } = await serviceConfigList({
                    page,
                    size: pageSize,
                });

                if (data.code !== 200) {
                    message.error(data.msg);
                    return;
                }

                const meta = data.meta;
                const defaultPageSize = DEFAULT_PAGINATION.pageSize ?? 20;
                const nextPage = meta?.page ?? page;
                const nextPageSize = meta?.size ?? pageSize;

                setPagination({
                    current: typeof nextPage === 'number' ? nextPage || 1 : Number(nextPage) > 0 ? Number(nextPage) : 1,
                    pageSize:
                        typeof nextPageSize === 'number'
                            ? nextPageSize || defaultPageSize
                            : Number(nextPageSize) > 0
                              ? Number(nextPageSize)
                              : defaultPageSize,
                    total: meta?.total ?? data.data?.length ?? 0,
                });
                setDataSource(data.data ?? []);
            } catch (error: any) {
                message.error(error?.response?.data?.msg || error?.message || 'Failed to fetch service configs');
            } finally {
                setLoading(false);
            }
        },
        [message, pagination.current, pagination.pageSize, setPagination],
    );

    useEffect(() => {
        fetchList();
    }, [fetchList]);

    const handleNavigateToDetail = useCallback(
        ({ id, mode, record }: { id?: string; mode?: DetailPageMode; record?: ServiceConfigItem }) => {
            const params = new URLSearchParams();
            if (id) params.set('id', id);
            if (mode) params.set('mode', mode);
            const search = params.toString();

            const options: NavigateOptions = {};
            if (record) {
                options.state = { record };
            }

            navigate(`${detailPath}${search ? `?${search}` : ''}`, options);
        },
        [detailPath, navigate],
    );

    const handleDelete = useCallback(
        async (config: ServiceConfigItem) => {
            setDeletingId(config.id);
            try {
                const { data } = await serviceConfigDelete(config.id);
                if (data.code !== 200) {
                    message.error(data.msg);
                    return;
                }

                message.success(formatMessage({ id: 'common.deleteOk' }));
                await fetchList(pagination.current as number, pagination.pageSize as number);
            } catch (error: any) {
                message.error(error?.response?.data?.msg || error?.message || 'Failed to delete service config');
            } finally {
                setDeletingId(undefined);
            }
        },
        [fetchList, formatMessage, message, pagination.current, pagination.pageSize],
    );

    const columns = useMemo<ProColumns<ServiceConfigItem>[]>(() => {
        const baseColumns: ProColumns<ServiceConfigItem>[] = [
            {
                dataIndex: 'index',
                title: 'No.',
                valueType: 'index',
                width: 60,
            },
            {
                dataIndex: 'key',
                title: <FormattedMessage id="serviceConfig.table.key" />,
                width: 200,
            },
            {
                dataIndex: 'type',
                title: <FormattedMessage id="serviceConfig.table.type" />,
                width: 120,
            },
            {
                dataIndex: 'value',
                title: <FormattedMessage id="serviceConfig.table.value" />,
                ellipsis: true,
                width: 180,
                render: (_, record) => {
                    const prettyValue = JSON.stringify(record.value ?? {}, null, 2);
                    if (!prettyValue || prettyValue === '{}') return '-';

                    const compactPreview = prettyValue.replace(/\s+/g, ' ');
                    const displayText = compactPreview.length > 80 ? `${compactPreview.slice(0, 80)}…` : compactPreview;

                    return (
                        <Popover
                            content={
                                <pre style={{ margin: 0, maxWidth: 480, whiteSpace: 'pre-wrap' }}>{prettyValue}</pre>
                            }
                            overlayStyle={{ maxWidth: 520 }}
                        >
                            <span
                                style={{
                                    display: 'inline-block',
                                    maxWidth: '100%',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    cursor: 'pointer',
                                }}
                            >
                                {displayText}
                            </span>
                        </Popover>
                    );
                },
            },
            {
                dataIndex: 'updated_at',
                title: <FormattedMessage id="serviceConfig.table.updatedAt" />,
                valueType: 'dateTime',
                width: 200,
            },
            {
                dataIndex: 'created_at',
                title: <FormattedMessage id="serviceConfig.table.createdAt" />,
                valueType: 'dateTime',
                width: 200,
            },
            {
                title: <FormattedMessage id="serviceConfig.table.actions" />,
                valueType: 'option',
                fixed: 'right',
                width: 200,
                align: 'center',
                render: (_, record) => {
                    const isBannerConfig =
                        record.key === THEATER_BANNER_CONFIG_KEY && record.module === THEATER_BANNER_CONFIG_MODULE;
                    const isActivity1011Config =
                        record.key === ACTIVITY_1011_CONFIG_KEY && record.module === ACTIVITY_1011_CONFIG_MODULE;

                    return [
                    <Permission permission={editPermission} key="detail">
                        <Tooltip title={formatMessage({ id: 'b.details' })}>
                            <Button
                                type="link"
                                size="small"
                                icon={<EyeOutlined />}
                                onClick={() => handleNavigateToDetail({ id: record.id, mode: 'detail', record })}
                            />
                        </Tooltip>
                    </Permission>,
                    <Divider type="vertical" key="d1" style={{ margin: 0 }} />,
                    <Permission permission={editPermission} key="edit">
                        <Tooltip title={formatMessage({ id: 'b.edit' })}>
                            <Button
                                type="link"
                                size="small"
                                icon={<EditOutlined />}
                                onClick={() => handleNavigateToDetail({ id: record.id, mode: 'edit', record })}
                            />
                        </Tooltip>
                    </Permission>,
                    <Divider type="vertical" key="d2" style={{ margin: 0 }} />,
                    <Permission permission={deletePermission} key="delete">
                        <Popconfirm
                            title={formatMessage({ id: 'serviceConfig.delete.confirm' })}
                            okText={formatMessage({ id: 'common.sure' })}
                            cancelText={formatMessage({ id: 'common.cancel' })}
                            okButtonProps={{ loading: deletingId === record.id }}
                            onConfirm={() => handleDelete(record)}
                        >
                            <Tooltip title={formatMessage({ id: 'common.delete' })}>
                                <Button
                                    type="link"
                                    size="small"
                                    icon={<DeleteOutlined />}
                                    danger
                                    loading={deletingId === record.id}
                                />
                            </Tooltip>
                        </Popconfirm>
                    </Permission>,
                    <Divider type="vertical" key="d3" style={{ margin: 0 }} />,
                    <Tooltip title={formatMessage({ id: 'serviceConfig.table.logs' })} key="log">
                        <Button
                            type="link"
                            size="small"
                            icon={<HistoryOutlined />}
                            onClick={() => {
                                setLogBizId(record.id);
                                setLogDrawerOpen(true);
                            }}
                        />
                    </Tooltip>,
                    isBannerConfig ? (
                        <>
                            <Divider type="vertical" key="d4" style={{ margin: 0 }} />
                            <Permission permission={THEATER_BANNER_PERMISSION} key="banner-perm">
                                <Tooltip title={formatMessage({ id: 'clientBanner.title' })} key="banner">
                                    <Button
                                        type="link"
                                        size="small"
                                        icon={<PictureOutlined />}
                                        onClick={() => navigate(bannerEditorPath)}
                                    />
                                </Tooltip>
                            </Permission>
                        </>
                    ) : null,
                    isActivity1011Config ? (
                        <>
                            <Divider type="vertical" key="d5" style={{ margin: 0 }} />
                            <Permission permission={ACTIVITY_1011_TASK_CONFIG_PERMISSION} key="activity1011-perm">
                                <Tooltip title={formatMessage({ id: 'activity1011Config.title' })} key="activity1011">
                                    <Button
                                        type="link"
                                        size="small"
                                        icon={<SettingOutlined />}
                                        onClick={() => navigate(activity1011ConfigPath)}
                                    />
                                </Tooltip>
                            </Permission>
                        </>
                    ) : null,
                ];
                },
            },
        ];

        baseColumns.splice(3, 0, {
            dataIndex: 'module',
            title: <FormattedMessage id="serviceConfig.table.module" />,
            width: 120,
            render: (dom) => dom || '-',
        });

        return baseColumns;
    }, [
        activity1011ConfigPath,
        bannerEditorPath,
        deletePermission,
        deletingId,
        editPermission,
        formatMessage,
        handleDelete,
        handleNavigateToDetail,
        navigate,
    ]);

    return (
        <Permission permission={permissionScope}>
            <ProCard>
                <AdminProTable<ServiceConfigItem>
                    bordered
                    columns={columns}
                    dataSource={dataSource}
                    loading={loading}
                    rowKey="id"
                    search={false}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        onChange: (page, pageSize) => {
                            setPagination({ current: page, pageSize });
                            fetchList(page, pageSize);
                        },
                    }}
                    toolBarRender={() => [
                        <Permission permission={addPermission} key="create">
                            <Button type="primary" onClick={() => handleNavigateToDetail({ mode: 'create' })}>
                                <FormattedMessage id="serviceConfig.create" />
                            </Button>
                        </Permission>,
                    ]}
                    options={{
                        reload: () => fetchList(pagination.current as number, pagination.pageSize as number),
                        density: true,
                        setting: true,
                    }}
                    scroll={{
                        x: 1200,
                    }}
                />
            </ProCard>
            <OperationLogDrawer
                open={logDrawerOpen}
                bizId={logBizId}
                onClose={() => {
                    setLogDrawerOpen(false);
                    setLogBizId(null);
                }}
            />
        </Permission>
    );
};

export default PageServiceConfig;
